import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import {
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AVAILABLE_ENTITY_TAGS,
  ENTITY_ID_PREFIX,
} from '../../../api/hexa/models/EntityStatisticsModels';
import {
  AVAILABLE_BUSINESS_BASE_TYPES,
  BusinessBaseType,
} from '../../../api/business_base/models/BusinessBaseStatisticsModels';
import { sgrColors } from '../../../ui/sgr/SgrColors';
import StatisticsDateRangePicker from '../../components/DateRangePicker';
import FilterFieldBox from './FilterFieldBox';

export const STANDARD_FIELD_PROPS = {
  variant: 'standard' as const,
  fullWidth: true,
};

export interface DateRangeState {
  startDate: string | null;
  setStartDate: (date: string | null) => void;
  endDate: string | null;
  setEndDate: (date: string | null) => void;
}

export const DateRangeFilter = ({ state }: { state: DateRangeState }) => {
  const { t } = useTranslation();
  return (
    <FilterFieldBox
      label={t('entity_statistics.date_picker_label')}
      icon={<CalendarMonthOutlinedIcon sx={{ fontSize: '0.875rem' }} />}
    >
      <StatisticsDateRangePicker
        dateBoundaries={{
          minDate: state.startDate ? dayjs(state.startDate) : null,
          maxDate: dayjs(Date.now()),
        }}
        startDate={state.startDate}
        setStartDate={state.setStartDate}
        endDate={state.endDate}
        setEndDate={state.setEndDate}
        slotProps={{ textField: STANDARD_FIELD_PROPS }}
      />
    </FilterFieldBox>
  );
};

export interface ResultsCountState {
  requestedResultsInput: string;
  handleRequestedResultsChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  MIN_REQUESTED_RESULTS: number;
  MAX_REQUESTED_RESULTS: number;
}

export const ResultsCountFilter = ({ state }: { state: ResultsCountState }) => {
  const { t } = useTranslation();
  return (
    <FilterFieldBox
      label={t('entity_statistics.results_count_label')}
      icon={<FormatListNumberedRoundedIcon sx={{ fontSize: '0.875rem' }} />}
    >
      <TextField
        type="number"
        {...STANDARD_FIELD_PROPS}
        value={state.requestedResultsInput}
        onChange={state.handleRequestedResultsChange}
        inputProps={{
          min: state.MIN_REQUESTED_RESULTS,
          max: state.MAX_REQUESTED_RESULTS,
        }}
      />
    </FilterFieldBox>
  );
};

export interface TagsFilterState {
  tags: string[];
  setTags: (tags: string[]) => void;
}

export const TagsFilter = ({ state }: { state: TagsFilterState }) => {
  const { t } = useTranslation();
  return (
    <FilterFieldBox
      label={t('entity_statistics.tags_filter_label')}
      icon={<LocalOfferOutlinedIcon sx={{ fontSize: '0.875rem' }} />}
    >
      <Select
        {...STANDARD_FIELD_PROPS}
        multiple
        displayEmpty
        value={state.tags}
        onChange={(event: SelectChangeEvent<string[]>) => {
          const value = event.target.value;
          state.setTags(typeof value === 'string' ? value.split(',') : value);
        }}
        renderValue={(selected: string[]) =>
          selected.length === 0
            ? t('entity_statistics.tags_filter_placeholder')
            : selected
                .map((tag) => t(`entity_statistics.tag_${tag}`))
                .join(', ')
        }
      >
        {AVAILABLE_ENTITY_TAGS.map((tag) => (
          <MenuItem key={tag} value={tag}>
            <Checkbox checked={state.tags.includes(tag)} size="small" />
            <ListItemText primary={t(`entity_statistics.tag_${tag}`)} />
          </MenuItem>
        ))}
      </Select>
    </FilterFieldBox>
  );
};

export interface OriginFilterState<O extends string> {
  origins: O[];
  setOrigins: (origins: O[]) => void;
}

interface OriginFilterProps<O extends string> {
  state: OriginFilterState<O>;
  options: readonly O[];
  /** Translation key prefix for each option, e.g. `entity_statistics.origin_` */
  labelKeyPrefix: string;
}

export const OriginFilter = <O extends string>({
  state,
  options,
  labelKeyPrefix,
}: OriginFilterProps<O>) => {
  const { t } = useTranslation();
  return (
    <FilterFieldBox
      label={t('entity_statistics.origin_filter_label')}
      icon={<SourceOutlinedIcon sx={{ fontSize: '0.875rem' }} />}
    >
      <Select
        {...STANDARD_FIELD_PROPS}
        multiple
        displayEmpty
        value={state.origins}
        onChange={(event: SelectChangeEvent<O[]>) => {
          const value = event.target.value;
          state.setOrigins(
            typeof value === 'string' ? (value.split(',') as O[]) : value
          );
        }}
        renderValue={(selected: O[]) =>
          selected.length === 0
            ? t('entity_statistics.origin_filter_placeholder')
            : selected
                .map((origin) => t(`${labelKeyPrefix}${origin}`))
                .join(', ')
        }
      >
        {options.map((origin) => (
          <MenuItem key={origin} value={origin}>
            <Checkbox checked={state.origins.includes(origin)} size="small" />
            <ListItemText primary={t(`${labelKeyPrefix}${origin}`)} />
          </MenuItem>
        ))}
      </Select>
    </FilterFieldBox>
  );
};

export interface TypesFilterState {
  types: BusinessBaseType[];
  setTypes: (types: BusinessBaseType[]) => void;
}

export const TypesFilter = ({ state }: { state: TypesFilterState }) => {
  const { t } = useTranslation();
  return (
    <FilterFieldBox
      label={t('business_base_statistics.types_filter_label')}
      icon={<CategoryOutlinedIcon sx={{ fontSize: '0.875rem' }} />}
    >
      <Select
        {...STANDARD_FIELD_PROPS}
        multiple
        displayEmpty
        value={state.types}
        onChange={(event: SelectChangeEvent<BusinessBaseType[]>) => {
          const value = event.target.value;
          state.setTypes(
            typeof value === 'string'
              ? (value.split(',') as BusinessBaseType[])
              : value
          );
        }}
        renderValue={(selected: BusinessBaseType[]) =>
          selected.length === 0
            ? t('business_base_statistics.types_filter_placeholder')
            : selected
                .map((type) => t(`business_base_statistics.type_${type}`))
                .join(', ')
        }
      >
        {AVAILABLE_BUSINESS_BASE_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            <Checkbox checked={state.types.includes(type)} size="small" />
            <ListItemText
              primary={t(`business_base_statistics.type_${type}`)}
            />
          </MenuItem>
        ))}
      </Select>
    </FilterFieldBox>
  );
};

export interface EntityIdFilterState {
  entityIdInput: string;
  handleEntityIdInputChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

export const EntityIdFilter = ({ state }: { state: EntityIdFilterState }) => {
  const { t } = useTranslation();
  return (
    <FilterFieldBox
      label={t('entity_statistics.entity_id_filter_label')}
      icon={<PersonIcon sx={{ fontSize: '0.875rem' }} />}
    >
      <TextField
        {...STANDARD_FIELD_PROPS}
        value={state.entityIdInput}
        onChange={state.handleEntityIdInputChange}
        placeholder={t('entity_statistics.entity_id_filter_placeholder')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">{ENTITY_ID_PREFIX}</InputAdornment>
          ),
        }}
      />
    </FilterFieldBox>
  );
};

export interface ValueFilterState {
  valueInput: string;
  handleValueInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const ValueFilter = ({
  state,
  label,
  placeholder,
}: {
  state: ValueFilterState;
  label: string;
  placeholder?: string;
}) => (
  <FilterFieldBox
    label={label}
    icon={<SearchRoundedIcon sx={{ fontSize: '0.875rem' }} />}
  >
    <TextField
      {...STANDARD_FIELD_PROPS}
      value={state.valueInput}
      onChange={state.handleValueInputChange}
      placeholder={placeholder}
    />
  </FilterFieldBox>
);

/**
 * Shows how long ago data refreshed by a scheduled job was last updated.
 * `lastUpdated` is the ISO timestamp of the last update; when it is null or
 * invalid an error indicator is shown instead.
 * `tooltip` receives the last update time formatted as DD-MM-YYYY HH:mm.
 */
export const LastUpdatedChip = ({
  lastUpdated,
  tooltip,
}: {
  lastUpdated: string | null;
  tooltip?: (updateTime: string) => ReactNode;
}) => {
  const { t } = useTranslation();
  const lastUpdate = lastUpdated ? dayjs(lastUpdated) : null;

  if (!lastUpdate?.isValid()) {
    return (
      <Tooltip
        title={t('entity_statistics.last_updated_chip_error_tooltip')}
        arrow
      >
        <Chip
          size="small"
          icon={<ErrorOutlineRoundedIcon />}
          label={t('entity_statistics.last_updated_chip_error')}
          sx={{
            flexShrink: 0,
            fontWeight: 600,
            color: sgrColors.rose,
            backgroundColor: sgrColors.light_red,
            border: `1px solid ${sgrColors.rose}`,
            '& .MuiChip-icon': { color: sgrColors.rose },
          }}
        />
      </Tooltip>
    );
  }

  const hoursAgo = Math.max(dayjs().diff(lastUpdate, 'hour'), 0);

  return (
    <Tooltip
      title={tooltip?.(lastUpdate.format('DD-MM-YYYY HH:mm')) ?? ''}
      arrow
    >
      <Chip
        size="small"
        icon={<AccessTimeRoundedIcon />}
        label={
          hoursAgo < 1
            ? t('entity_statistics.last_updated_chip_recent')
            : t('entity_statistics.last_updated_chip', { count: hoursAgo })
        }
        sx={{
          flexShrink: 0,
          fontWeight: 600,
          color: sgrColors.navy,
          backgroundColor: sgrColors.chips_background,
          border: `1px solid ${sgrColors.grey_cloud}`,
          '& .MuiChip-icon': { color: sgrColors.navy },
        }}
      />
    </Tooltip>
  );
};

export const ChartFrame = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      height: '100%',
      boxSizing: 'border-box',
      padding: '0.75rem 0.5rem',
    }}
  >
    {children}
  </Box>
);
